import React, { useContext } from 'react';
import { Table } from 'antd';
import { ColumnProps } from 'antd/lib/table';

import EmployeeUtilizationContext from '@totem/components/ticketing/employeeUtilization/employeeUtilizationContext';
import { DepartmentSummary } from '@totem/components/ticketing/employeeUtilization/models';
import { calculateWorkdays } from '@totem/components/ticketing/employeeUtilization/utilities';
import Hours from '@totem/components/ui_components/widgets/hours/hours';
import { isNotNull } from '@totem/utilities/common';
import { sortStringAscending } from '@totem/utilities/tableUtilities';

const DepartmentUtilizationTable = () => {
  const { rawData, loading, input, holidays } = useContext(
    EmployeeUtilizationContext,
  );

  const calculateWorkingDays = () => {
    if (
      isNotNull(input) &&
      isNotNull(input.startTime) &&
      isNotNull(input.endTime)
    ) {
      return calculateWorkdays(input.startTime, input.endTime, holidays);
    }
    return 1;
  };

  const getDepartmentWeeklyAvailableHours = (department: string) => {
    const departmentItem = rawData.departments.find(
      (chk) => chk.department === department,
    );
    if (isNotNull(departmentItem)) {
      return departmentItem.availableHours;
    }
    return 40;
  };

  const calculateCapacity = (department: string) => {
    const availableHoursPerWeek = getDepartmentWeeklyAvailableHours(department);
    const numberOfWorkDays = calculateWorkingDays();
    const capacity = (availableHoursPerWeek / 5) * numberOfWorkDays;
    return capacity;
  };

  const calculateAvailableHours = (record: DepartmentSummary) => {
    const capacity = calculateCapacity(record.department);
    const availableHours = capacity - record.totalHoursLogged;
    return availableHours;
  };

  const calculateUtilization = (record: DepartmentSummary) => {
    const capacity = calculateCapacity(record.department);
    if (record.totalHoursLogged > 0) {
      const utilization = (record.totalHoursLogged / capacity) * 100;
      return utilization;
    }
    return 0;
  };

  const calculateBillableUtilization = (record: DepartmentSummary) => {
    const capacity = calculateCapacity(record.department);
    if (capacity > 0) {
      const utilization = (record.totalBillable / capacity) * 100;
      return utilization;
    }

    return 1;
  };

  const calculateSpentTimeBillable = (record: DepartmentSummary) => {
    if (record.totalHoursLogged === 0) {
      return 0;
    }
    const percentTimeBillable =
      (record.totalBillable / record.totalHoursLogged) * 100;
    return percentTimeBillable;
  };

  const columns: ColumnProps<DepartmentSummary>[] = [
    {
      title: 'Department',
      dataIndex: 'department',
      key: 'department',
      render: (_, record: DepartmentSummary) => (
        <span>{record.department}</span>
      ),
      showSorterTooltip: true,
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        sortStringAscending(compA.department, compB.department),
    },
    {
      title: 'Capacity (Hours)',
      dataIndex: 'capacity',
      key: 'capacity',
      render: (_, record: DepartmentSummary) => (
        <Hours amount={calculateCapacity(record.department)} />
      ),
      showSorterTooltip: false,
    },
    {
      title: 'Available Hours',
      dataIndex: 'availableHours',
      key: 'availableHours',
      render: (_, record: DepartmentSummary) => (
        <Hours amount={calculateAvailableHours(record)} />
      ),
      showSorterTooltip: false,
    },
    {
      title: 'Spent Time (Hours)',
      dataIndex: 'totalHoursLogged',
      key: 'totalHoursLogged',
      render: (_, record: DepartmentSummary) => (
        <Hours amount={record.totalHoursLogged} />
      ),
      showSorterTooltip: false,
    },
    {
      title: 'Billable Time (Hours)',
      dataIndex: 'totalBillable',
      key: 'totalBillable',
      render: (_, record: DepartmentSummary) => (
        <Hours amount={record.totalBillable} />
      ),
      showSorterTooltip: false,
    },
    {
      title: 'Non-Billable Time (Hours)',
      dataIndex: 'totalUnBillable',
      key: 'totalUnBillable',
      render: (_, record: DepartmentSummary) => (
        <Hours amount={record.totalUnBillable} />
      ),
      showSorterTooltip: false,
    },
    {
      title: 'Total Utilization',
      dataIndex: 'utilization',
      key: 'utilization',
      render: (_, record: DepartmentSummary) => (
        <span>{calculateUtilization(record).toFixed(2)}%</span>
      ),
      showSorterTooltip: false,
    },
    {
      title: 'Billable Utilization',
      dataIndex: 'billableUtilization',
      key: 'billableUtilization',
      render: (_, record: DepartmentSummary) => (
        <span>{calculateBillableUtilization(record).toFixed(2)}%</span>
      ),
      showSorterTooltip: false,
    },
    {
      title: '% Time Billable',
      dataIndex: 'percentSpentTimeBillable',
      key: 'percentSpentTimeBillable',
      render: (_, record: DepartmentSummary) => (
        <span>{calculateSpentTimeBillable(record).toFixed(2)}%</span>
      ),
      showSorterTooltip: false,
    },
  ];

  return (
    <Table
      showSorterTooltip
      columns={columns}
      dataSource={
        isNotNull(rawData) && isNotNull(rawData.departmentSummary)
          ? rawData.departmentSummary
          : []
      }
      loading={loading}
      pagination={{
        showSizeChanger: true,
      }}
      rowKey={(record) => record.department}
    />
  );
};

export default DepartmentUtilizationTable;
